import { Fragment, useEffect, useMemo, useRef } from "react";
import { usePaginatedQuery } from "convex/react";
import { Loader2 } from "lucide-react";
import { api } from "../../../convex/_generated/api";
import type { Id } from "../../../convex/_generated/dataModel";
import { Button } from "@/components/ui/button";
import { MessageItem } from "./MessageItem";

function formatDayLabel(ts: number): string {
  const d = new Date(ts);
  const now = new Date();
  const startOfDay = (x: Date) =>
    new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diffDays = Math.round(
    (startOfDay(now) - startOfDay(d)) / (24 * 60 * 60 * 1000),
  );
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7)
    return d.toLocaleDateString([], { weekday: "long" });
  return d.toLocaleDateString([], {
    month: "short",
    day: "numeric",
    year: d.getFullYear() === now.getFullYear() ? undefined : "numeric",
  });
}

function sameDay(a: number, b: number): boolean {
  const da = new Date(a);
  const db = new Date(b);
  return (
    da.getFullYear() === db.getFullYear() &&
    da.getMonth() === db.getMonth() &&
    da.getDate() === db.getDate()
  );
}

export function MessageList({
  conversationId,
  conversationKind,
  meId,
}: {
  conversationId: Id<"conversations">;
  conversationKind: "dm" | "room";
  meId: Id<"users">;
}) {
  const { results, status, loadMore } = usePaginatedQuery(
    api.messages.listPaginated,
    { conversationId },
    { initialNumItems: 50 },
  );

  const containerRef = useRef<HTMLDivElement | null>(null);
  const lastCount = useRef(0);
  const stickToBottom = useRef(true);

  const messages = useMemo(() => results.slice().reverse(), [results]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    if (messages.length > lastCount.current && stickToBottom.current) {
      el.scrollTop = el.scrollHeight;
    }
    lastCount.current = messages.length;
  }, [messages.length]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [conversationId]);

  const onScroll = () => {
    const el = containerRef.current;
    if (!el) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    stickToBottom.current = distance < 80;
  };

  return (
    <div
      ref={containerRef}
      onScroll={onScroll}
      className="flex-1 min-h-0 overflow-y-auto px-4 py-3"
    >
      {status === "CanLoadMore" || status === "LoadingMore" ? (
        <div className="flex justify-center py-2">
          <Button
            variant="ghost"
            size="sm"
            disabled={status === "LoadingMore"}
            onClick={() => loadMore(50)}
          >
            {status === "LoadingMore" ? (
              <Loader2 className="size-4 animate-spin" />
            ) : null}
            Load older messages
          </Button>
        </div>
      ) : null}
      {status === "LoadingFirstPage" ? (
        <div className="flex justify-center py-8">
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
        </div>
      ) : null}
      <div className="flex flex-col">
        {messages.map((m, idx) => {
          const prev = messages[idx - 1];
          const next = messages[idx + 1];
          const sameAuthorPrev = prev && prev.authorId === m.authorId;
          const closeToPrev =
            prev && m._creationTime - prev._creationTime < 5 * 60 * 1000;
          const showHeader = !(sameAuthorPrev && closeToPrev);

          const sameAuthorNext = next && next.authorId === m.authorId;
          const closeToNext =
            next && next._creationTime - m._creationTime < 5 * 60 * 1000;
          const isLastOfGroup = !(sameAuthorNext && closeToNext);

          const showDaySep =
            !prev || !sameDay(prev._creationTime, m._creationTime);

          const tightTop = sameAuthorPrev && closeToPrev && !showDaySep;

          return (
            <Fragment key={m._id}>
              {showDaySep ? (
                <div className="flex justify-center my-3">
                  <span className="rounded-full bg-muted px-3 py-1 text-[11px] text-muted-foreground">
                    {formatDayLabel(m._creationTime)}
                  </span>
                </div>
              ) : null}
              <div className={tightTop ? "mt-0.5" : "mt-2"}>
                <MessageItem
                  message={m}
                  meId={meId}
                  conversationKind={conversationKind}
                  showHeader={showHeader}
                  isLastOfGroup={isLastOfGroup}
                />
              </div>
            </Fragment>
          );
        })}
        {messages.length === 0 && status !== "LoadingFirstPage" ? (
          <div className="text-center text-sm text-muted-foreground py-12">
            No messages yet. Say hi!
          </div>
        ) : null}
      </div>
    </div>
  );
}
